import type { BoxGeometry } from 'three';

/**
 * Rewrite the UVs of an axis-aligned `BoxGeometry` so each face's UV
 * coordinates encode the vertex's position in its PARENT GROUP (in
 * meters), instead of the default [0,1]×[0,1] per-face mapping.
 *
 * Motivation
 * ----------
 * A facade is segmented into pillars, sills, and lintels around each
 * opening. Every slab is its own `BoxGeometry`, and `BoxGeometry`
 * gives every face a fresh [0,1]×[0,1] UV region regardless of size.
 * Paired with a repeating texture that uses a fixed `repeat`, a 1 m
 * pillar and a 6 m pillar each try to fit the same repeat count over
 * different physical lengths — the brick texture ends up compressed
 * on the narrow slab and stretched on the wide one, and adjacent
 * slabs never align at their shared edges (user-reported in the
 * "different UV scaling across subsections" screenshot).
 *
 * Fix: bake UVs in the PARENT-GROUP coordinate system so that two
 * adjacent slabs on the same facade share a single continuous UV
 * space. Under a repeating texture whose `repeat = (1/tile, 1/tile)`,
 * one texture tile spans `tile` meters of wall regardless of where
 * the slab boundaries fall — bricks line up seamlessly from pillar
 * to sill to lintel.
 *
 * UV axis assignment per face (`BoxGeometry` orders faces as
 * +X, -X, +Y, -Y, +Z, -Z, with 4 vertices each, 24 vertices total):
 *   ±X faces (sides):      u = frameZ, v = frameY
 *   ±Y faces (cap/bottom): u = frameX, v = frameZ
 *   ±Z faces (front/back): u = frameX, v = frameY
 *
 * These axis assignments mean the dominant visible face on a facade
 * (the outward ±Z) uses `(facade-u, world-y)` — exactly what aligns
 * neighbouring wall pieces. The cap and side mappings keep the tile
 * size consistent even where those faces are exposed (e.g. the top
 * of a parapet or the side of a jamb) without needing per-face
 * branching at the call site.
 *
 * The `centerX/Y/Z` arguments are the mesh's position in the parent
 * group — i.e. the same triple you pass to `mesh.position.set`. The
 * helper does NOT touch the geometry's normals or indices, so it's
 * safe to call immediately after `new BoxGeometry(...)` and before
 * the mesh is added to its parent.
 */
export function bakeFrameWorldUVs(
  geom: BoxGeometry,
  centerX: number,
  centerY: number,
  centerZ: number,
): void {
  const pos = geom.attributes.position;
  const uv = geom.attributes.uv;
  if (!pos || !uv) return;
  for (let i = 0; i < pos.count; i++) {
    const fx = centerX + pos.getX(i);
    const fy = centerY + pos.getY(i);
    const fz = centerZ + pos.getZ(i);
    // Faces are emitted in order +X, -X, +Y, -Y, +Z, -Z, 4 verts each.
    const face = Math.floor(i / 4);
    let u: number;
    let v: number;
    if (face === 0 || face === 1) {
      u = fz;
      v = fy;
    } else if (face === 2 || face === 3) {
      u = fx;
      v = fz;
    } else {
      u = fx;
      v = fy;
    }
    uv.setXY(i, u, v);
  }
  uv.needsUpdate = true;
}
